import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CHECKIN_REASONS } from '../../constants/checkinReasons';
import { addDays, dayLabel, weekday } from '../../utils/tokyoDate';
import CheckinMemoField from '../goals/CheckinMemoField';
import '../../styles/checkinPopup.css';

const NUM = ['①', '②', '③', '④', '⑤'];

const Check = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12l5 5 9-10" />
  </svg>
);
const Cross = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const LABEL = { today: '今日', yesterday: '昨日' };
const OTHER = { today: 'yesterday', yesterday: 'today' };

/** Header / button copy. `day` = the day on screen (form) or the day just saved (prompt / done). */
const copyFor = (step, day, isSaved, isOff) => {
  const L = LABEL[day];
  if (isOff && step !== 'form') {
    // 「この日は休み」 just pressed: same two screens as a save, but nothing to summarise
    const O = LABEL[OTHER[day]];
    return step === 'prompt'
      ? { eyebrow: `${L}は休み`, title: `${O}はどうでしたか？`, primary: `${O}の振り返りをする`, secondary: '今日はここまでにする' }
      : { eyebrow: `${L}は休み`, title: `${L}を休みとして記録しました`, primary: 'ホームへ' };
  }
  if (step === 'form') {
    return { eyebrow: `${L}の振り返り`, title: `${L}はどうでしたか？`, primary: isSaved ? '記録を更新する' : '記録をする' };
  }
  if (step === 'prompt') {
    const O = LABEL[OTHER[day]];
    return {
      eyebrow: `${L}の振り返り 完了`,
      title: `${O}はどうでしたか？`,
      primary: `${O}の振り返りをする`,
      secondary: '今日はここまでにする',
    };
  }
  return { eyebrow: `${L}の振り返り 完了`, title: `${L}の振り返りを記録しました`, primary: 'ホームへ', secondary: '記録を見直す' };
};

/** Saved check-in → form state, so a recorded day opens filled in and can be fixed. */
const fromSaved = (record, actions) => {
  const ans = {};
  const why = {};
  const memo = {};
  const byName = Object.fromEntries((record?.entries || []).map((e) => [e.actionName, e]));
  actions.forEach((a) => {
    const e = byName[a.name];
    if (!e) return;
    ans[a.name] = e.status === 'done' ? 'done' : 'miss';
    if (e.status !== 'done') {
      // A chat reason can be "chip / free text"; records from before the 2026-10-26 relabel carry
      // old chip text. Either way nothing is dropped: a non-chip reason opens as その他 and its text
      // goes to ひと言 (unless ひと言 already has something).
      const reason = String(e.reason || '');
      const [head, ...rest] = reason.split(' / ');
      const isChip = CHECKIN_REASONS.some((r) => r.label === head);
      why[a.name] = isChip ? head : 'その他';
      const extra = isChip ? rest.join(' / ') : reason;
      if (e.memo || extra) memo[a.name] = e.memo || extra;
    }
  });
  return { ans, why, memo };
};

const dayText = (ymd) => `${dayLabel(ymd)}（${weekday(ymd)}）`;

/**
 * Home 行動記録 popup — UI 案 B (docs/行動記録ポップアップ_B案確定版.html), as drawn:
 *   ① today → ② 「昨日はまだ記録がありません」 (only if yesterday is unrecorded) → ③ yesterday → ④ done.
 * 2026-10-27: the 昨日／今日 tabs are buttons. Either day can be recorded first, and a day that is
 * already saved opens filled in so it can be fixed (「記録を更新する」). After a save the popup offers
 * the other day only when that day is still unrecorded.
 * Differences decided by the owner (2026-10-26): no 「あとで記録する」 — × is the only way out, and
 * closing is for now only (it shows again next time ホーム opens until today is recorded).
 * `recordByDay` = {[ymd]: saved checkin} for today and yesterday; `onSave(day, entries)` saves one day.
 * 2026-10-28: 「この日は休み」 (`onMarkOff(day)`) — a day off (休み・外部の仕事) is recorded as a shift day
 * off instead of できた／できなかった: no reminder push that night, 「休」 in the 7-day strip. `offByDay` =
 * {[ymd]: true} from the shift (weekly days off included); an off day is not offered for recording.
 * Rendered into `.kuroco-shell` like CheckinSheet, so the tab bar doesn't cover it.
 */
export default function CheckinPopup({ today, actions, recordByDay, offByDay, onSave, onMarkOff, onDismiss, onDone }) {
  const yesterday = addDays(today, -1);
  const ymd = { today, yesterday };
  const saved = { today: Boolean(recordByDay?.[today]), yesterday: Boolean(recordByDay?.[yesterday]) };
  const [off, setOff] = useState({ today: Boolean(offByDay?.[today]), yesterday: Boolean(offByDay?.[yesterday]) });
  // nothing left to ask for on a day that is recorded or off
  const settled = (day) => saved[day] || off[day];
  // Start on the first unrecorded day (today first); both recorded → today, to fix it.
  const [step, setStep] = useState('form'); // 'form' | 'prompt' | 'done'
  const [k, setK] = useState(() => (!settled('today') ? 'today' : !settled('yesterday') ? 'yesterday' : 'today'));
  const [init] = useState(() => {
    const t = fromSaved(recordByDay?.[today], actions);
    const y = fromSaved(recordByDay?.[yesterday], actions);
    return { answers: { today: t.ans, yesterday: y.ans }, reasons: { today: t.why, yesterday: y.why }, memos: { today: t.memo, yesterday: y.memo } };
  });
  const [answers, setAnswers] = useState(init.answers); // day → name → 'done' | 'miss'
  const [reasons, setReasons] = useState(init.reasons);
  const [memos, setMemos] = useState(init.memos);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isForm = step === 'form';
  const offOnly = (day) => off[day] && !saved[day]; // off and nothing recorded → the 休み screens
  const c = copyFor(step, k, saved[k], offOnly(k));
  const ans = answers[k];
  const why = reasons[k];

  const openDay = (day) => {
    if (saving) return;
    setError(null);
    setK(day);
    setStep('form');
  };

  // Every action needs an answer; できなかった also needs a reason (ひと言 stays optional).
  const missing = isForm ? actions.filter((a) => !ans[a.name] || (ans[a.name] === 'miss' && !why[a.name])).length : 0;

  const setFor = (setter, name, v) => setter((p) => ({ ...p, [k]: { ...p[k], [name]: v } }));

  const entriesFor = (day) =>
    actions.map((a, i) => {
      const miss = answers[day][a.name] === 'miss';
      return {
        actionId: a.id ?? i + 1,
        actionName: a.name,
        status: answers[day][a.name],
        reason: miss ? reasons[day][a.name] || null : null,
        memo: miss ? (memos[day][a.name] || '').trim() || null : null,
      };
    });

  const save = async () => {
    if (missing || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(ymd[k], entriesFor(k));
      // the other day already recorded (or off) → nothing to prompt for: straight to the completion screen
      setStep(settled(OTHER[k]) ? 'done' : 'prompt');
    } catch (err) {
      setError(err?.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 「この日は休み」: the day becomes a shift day off (no answers). Then the same flow as a save.
  const markOff = async (value = true) => {
    if (saving || !onMarkOff) return;
    setSaving(true);
    setError(null);
    try {
      await onMarkOff(ymd[k], value); // value=false: 「出勤した日として記録する」 undoes the day off on the server too
      const next = { ...off, [k]: value };
      setOff(next);
      if (value) setStep(saved[OTHER[k]] || next[OTHER[k]] ? 'done' : 'prompt');
    } catch (err) {
      setError(err?.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const primary = () => {
    if (step === 'prompt') openDay(OTHER[k]);
    else if (step === 'done') onDone();
    else void save();
  };
  const secondary = () => {
    if (step === 'done') openDay(k); // 記録を見直す
    else onDone(); // prompt: ◯はここまでにする
  };
  // × before today is recorded (or off) → the "you can record later" toast; otherwise just close
  const close = () => (settled('today') ? onDone() : onDismiss());

  const renderSummary = (day, label) => {
    const dayAns = answers[day];
    const yesN = actions.filter((a) => dayAns[a.name] === 'done').length;
    return (
      <div className="cp-sum">
        <div className="cp-sum-h">
          <span>{label}</span>
          <span className="cp-sum-n">できた {yesN} / {actions.length}</span>
        </div>
        {actions.map((a) => {
          const ok = dayAns[a.name] === 'done';
          return (
            <div className="cp-sum-row" key={a.actionId || a.name}>
              <span className={`cp-sum-ic${ok ? ' is-ok' : ''}`}>{ok ? <Check size={12} /> : <Cross size={10} />}</span>
              <span className="cp-sum-name">{a.name}</span>
              <span className="cp-sum-why">{ok ? '' : reasons[day][a.name] || ''}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const host = typeof document !== 'undefined' ? document.querySelector('.kuroco-shell') || document.body : null;
  const popup = (
    <div className="cp-mask" role="presentation">
      <div className="cp-sheet" role="dialog" aria-modal="true" aria-label={c.eyebrow}>
        <div className="cp-grab" />
        <div className="cp-head">
          <div className="cp-head-t">
            <span className="cp-eyebrow">{c.eyebrow}</span>
            <span className="cp-title">{c.title}</span>
          </div>
          <button type="button" className="cp-x" aria-label="閉じる" onClick={close}>
            <Cross size={16} />
          </button>
        </div>

        <div className="cp-tabs">
          {['yesterday', 'today'].map((id) => {
            const current = isForm && id === k;
            return (
              <button
                key={id}
                type="button"
                className={`cp-tab${current ? ' is-current' : ''}${settled(id) ? ' is-saved' : ''}`}
                aria-pressed={current}
                disabled={saving}
                onClick={() => openDay(id)}
              >
                {saved[id] && <Check size={14} />}
                <span>{offOnly(id) ? `${LABEL[id]}（休み）` : LABEL[id]}</span>
              </button>
            );
          })}
        </div>

        <div className="cp-body">
          {isForm && offOnly(k) ? (
            <div className="cp-done">
              <div className="cp-note">
                <span className="cp-note-ic">休</span>
                <span>{LABEL[k]} {dayText(ymd[k])}は休みの日です。記録は要りません。</span>
              </div>
              <div className="cp-lead">出勤した日なら、下のボタンから記録できます。</div>
            </div>
          ) : isForm ? (
            <>
              <div className="cp-lead">
                {LABEL[k]} {dayText(ymd[k])}の行動ごとに「できた／できなかった」を振り返りましょう。
                {saved[k] && ' 記録済みの内容を直せます。'}
              </div>
              <div className="cp-list">
                {actions.map((a, i) => {
                  const v = ans[a.name];
                  const sub = a.when || a.cadence || '';
                  return (
                    <div className="cp-item" key={a.actionId || a.name}>
                      <div className="cp-row">
                        <div className="cp-row-t">
                          <span className="cp-name">{NUM[i] || `${i + 1}.`} {a.name}</span>
                          {sub && <span className="cp-when">{sub}</span>}
                        </div>
                        <button
                          type="button"
                          className={`cp-yn cp-yes${v === 'done' ? ' is-on' : ''}`}
                          aria-label="できた"
                          aria-pressed={v === 'done'}
                          onClick={() => setFor(setAnswers, a.name, 'done')}
                        >
                          <Check />
                        </button>
                        <button
                          type="button"
                          className={`cp-yn cp-no${v === 'miss' ? ' is-on' : ''}`}
                          aria-label="できなかった"
                          aria-pressed={v === 'miss'}
                          onClick={() => setFor(setAnswers, a.name, 'miss')}
                        >
                          <Cross />
                        </button>
                      </div>
                      {v === 'miss' && (
                        <div className="cp-why">
                          <span className="cp-why-l">できなかった理由 <span className="cp-req">*</span></span>
                          <div className="cp-chips">
                            {CHECKIN_REASONS.map((r) => (
                              <button
                                key={r.label}
                                type="button"
                                className={`cp-chip${why[a.name] === r.label ? ' is-on' : ''}`}
                                aria-pressed={why[a.name] === r.label}
                                onClick={() => setFor(setReasons, a.name, r.label)}
                              >
                                {r.label}
                              </button>
                            ))}
                          </div>
                          <CheckinMemoField
                            id={`cp-memo-${k}-${i}`}
                            value={memos[k][a.name]}
                            onChange={(val) => setFor(setMemos, a.name, val)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="cp-done">
              <div className="cp-done-hero">
                <span className="cp-done-ic">{offOnly(k) ? '休' : <Check size={22} />}</span>
                <div className="cp-done-t">
                  <span className="cp-done-h">{offOnly(k) ? '休みとして記録しました' : '記録しました'}</span>
                  <span className="cp-done-d">{`${LABEL[k]} ${dayText(ymd[k])}`}{offOnly(k) ? '' : 'の行動'}</span>
                </div>
              </div>
              {!offOnly(k) && renderSummary(k, `${LABEL[k]} ${dayText(ymd[k])}`)}
              {step === 'prompt' && (
                <div className="cp-note">
                  <span className="cp-note-ic">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M4 10h16M9 3v4M15 3v4" /></svg>
                  </span>
                  <span>{LABEL[OTHER[k]]} {dayText(ymd[OTHER[k]])}はまだ記録がありません</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="cp-foot">
          {error && <div className="cp-err" role="alert">{error}</div>}
          {isForm && offOnly(k) ? (
            <button type="button" className="cp-primary" disabled={saving} onClick={() => markOff(false)}>
              {saving ? '保存中…' : '出勤した日として記録する'}
            </button>
          ) : (
            <button type="button" className="cp-primary" disabled={Boolean(missing) || saving} onClick={primary}>
              {saving ? '保存中…' : c.primary}
            </button>
          )}
          {isForm && !off[k] && !saved[k] && onMarkOff && (
            <button type="button" className="cp-secondary" disabled={saving} onClick={() => markOff(true)}>
              {LABEL[k]}は休み（記録しない）
            </button>
          )}
          {c.secondary && (
            <button type="button" className="cp-secondary" onClick={secondary}>{c.secondary}</button>
          )}
        </div>
      </div>
    </div>
  );
  return host ? createPortal(popup, host) : popup;
}
